export const DRAIN_HOST_PROTOCOL_VERSION = 1 as const;
export const DRAIN_HOST_HELLO_TYPE = "ls-upload-drain-hello" as const;
export const DRAIN_HOST_STATUS_TYPE = "ls-upload-drain-status" as const;
/**
 * F-17: parent → child "wake the drain now".
 *
 * The embedding host can see that uploads need attention (`needsAttention` on the status message)
 * but had no way to ask the child to try again — its snackbar could only tell the user to go and
 * reopen a link somewhere else. This is the immediate-retry accelerator on top of R-0's automatic
 * retry-forever schedule; it never changes what is retried or how, only when the next pass starts.
 *
 * Additive by construction: it is a NEW message type on the same version, carrying the same nonce
 * binding as every other message, so a child that does not yet handle it simply ignores it and the
 * host degrades to the automatic schedule.
 */
export const DRAIN_HOST_WAKE_TYPE = "ls-upload-drain-wake" as const;

/**
 * LSP-19663: child → parent "my message listener is registered, send the hello".
 *
 * The parent cannot GUESS when the child is listening. The child registers its listener only after
 * admission (runtime config fetch + IndexedDB schema + persistence), which in a production bundle
 * completes AFTER the iframe's load event — so a hello posted on that event lands in a window with
 * no listener, and postMessage neither queues nor retries. The child announcing itself removes the
 * guess entirely.
 *
 * Additive by construction: a NEW type on the same version, so an older parent ignores it (and
 * keeps its load-event hello) and an older child never sends it.
 */
export const DRAIN_HOST_READY_TYPE = "ls-upload-drain-ready" as const;

export type DrainHostState = "idle" | "draining" | "yielded" | "unavailable";

export interface DrainHostHello {
	type: typeof DRAIN_HOST_HELLO_TYPE;
	version: typeof DRAIN_HOST_PROTOCOL_VERSION;
	nonce: string;
}

export interface DrainHostStatusMessage {
	type: typeof DRAIN_HOST_STATUS_TYPE;
	version: typeof DRAIN_HOST_PROTOCOL_VERSION;
	nonce: string;
	sequence: number;
	state: DrainHostState;
	pendingTotal: number;
	needsAttention: boolean;
	byKind: Readonly<Record<string, number>>;
}

/** F-17: parent → child immediate-retry request. Same nonce binding as the hello. */
export interface DrainHostWake {
	type: typeof DRAIN_HOST_WAKE_TYPE;
	version: typeof DRAIN_HOST_PROTOCOL_VERSION;
	nonce: string;
}

/** LSP-19663: child → parent handshake invitation. Carries no nonce — the hello is what delivers it. */
export interface DrainHostReady {
	type: typeof DRAIN_HOST_READY_TYPE;
	version: typeof DRAIN_HOST_PROTOCOL_VERSION;
}

export interface DrainHostValidationState {
	lastSequence: number;
}

export interface DrainHostValidationExpectation {
	origin: string;
	source: unknown;
	nonce: string;
}

export type DrainHostValidationResult =
	| { kind: "accepted"; status: DrainHostStatusMessage }
	| { kind: "replay" }
	| { kind: "origin-mismatch" }
	| { kind: "source-mismatch" }
	| { kind: "invalid-payload" };

export type DrainHostHelloValidationResult =
	| { kind: "accepted"; nonce: string }
	| { kind: "origin-mismatch" }
	| { kind: "source-mismatch" }
	| { kind: "invalid-payload" };

export type DrainHostReadyValidationResult =
	| { kind: "accepted" }
	| { kind: "origin-mismatch" }
	| { kind: "source-mismatch" }
	| { kind: "invalid-payload" };

export type DrainHostWakeValidationResult =
	| { kind: "accepted" }
	| { kind: "origin-mismatch" }
	| { kind: "source-mismatch" }
	| { kind: "invalid-payload" };

export function createDrainHostHello(nonce: string): DrainHostHello {
	return {
		type: DRAIN_HOST_HELLO_TYPE,
		version: DRAIN_HOST_PROTOCOL_VERSION,
		nonce
	};
}

/** LSP-19663: the child's "I am listening now" invitation. */
export function createDrainHostReady(): DrainHostReady {
	return {
		type: DRAIN_HOST_READY_TYPE,
		version: DRAIN_HOST_PROTOCOL_VERSION
	};
}

/**
 * LSP-19663 parent-side trust boundary for the child's ready. There is no nonce to bind against yet
 * — the ready is what triggers the parent to send one — so origin and source identity are the whole
 * check, exactly as they are for the first status message.
 */
export function validateDrainHostReadyEvent(
	event: Pick<MessageEvent, "origin" | "source" | "data">,
	expectation: Pick<DrainHostValidationExpectation, "origin" | "source">
): DrainHostReadyValidationResult {
	if (event.origin !== expectation.origin) return { kind: "origin-mismatch" };
	// The expectation must name a real window first. A bare `!==` accepts `null === null`, and the
	// parent passes `frameElement.value?.contentWindow ?? null` — so with no frame window at all, an
	// event carrying no source would pass the identity check at the one moment it matters most. Same
	// shape as validateDrainHostWakeEvent's guard on an absent nonce.
	if (expectation.source === null || expectation.source === undefined) return { kind: "invalid-payload" };
	if (event.source !== expectation.source) return { kind: "source-mismatch" };
	if (typeof event.data !== "object" || event.data === null || Array.isArray(event.data)) return { kind: "invalid-payload" };
	const candidate = event.data as Partial<DrainHostReady>;
	if (
		!hasExactKeys(candidate, ["type", "version"]) ||
		candidate.type !== DRAIN_HOST_READY_TYPE ||
		candidate.version !== DRAIN_HOST_PROTOCOL_VERSION
	) {
		return { kind: "invalid-payload" };
	}
	return { kind: "accepted" };
}

/** F-17: the parent's "try the stuck uploads again now" message. */
export function createDrainHostWake(nonce: string): DrainHostWake {
	return {
		type: DRAIN_HOST_WAKE_TYPE,
		version: DRAIN_HOST_PROTOCOL_VERSION,
		nonce
	};
}

/**
 * F-17 child-side trust boundary for the wake. Identical identity checks to the hello — origin,
 * source, exact wire shape — plus the nonce the child already learned from that hello, so only the
 * parent this child is bound to can trigger a pass. Carries no sequence: a wake is idempotent (it
 * asks for a drain that the child would eventually run anyway), so a replayed one costs at most one
 * extra pass and must never be rejected as stale the way a status message is.
 */
export function validateDrainHostWakeEvent(
	event: Pick<MessageEvent, "origin" | "source" | "data">,
	expectation: DrainHostValidationExpectation
): DrainHostWakeValidationResult {
	if (event.origin !== expectation.origin) return { kind: "origin-mismatch" };
	if (event.source !== expectation.source) return { kind: "source-mismatch" };
	if (typeof event.data !== "object" || event.data === null || Array.isArray(event.data)) return { kind: "invalid-payload" };
	const candidate = event.data as Partial<DrainHostWake>;
	// Both sides must be a NON-EMPTY STRING before they are compared. A bare `!==` accepts
	// `undefined === undefined`, so a child that has not yet received a hello (no nonce to bind
	// against) would accept a wake carrying no nonce at all — the one moment the binding is supposed
	// to be strictest. Same for an empty string, which is falsy-but-equal on both sides.
	if (typeof expectation.nonce !== "string" || expectation.nonce.length === 0) return { kind: "invalid-payload" };
	if (
		!hasExactKeys(candidate, ["type", "version", "nonce"]) ||
		candidate.type !== DRAIN_HOST_WAKE_TYPE ||
		candidate.version !== DRAIN_HOST_PROTOCOL_VERSION ||
		typeof candidate.nonce !== "string" ||
		candidate.nonce.length === 0 ||
		candidate.nonce !== expectation.nonce
	) {
		return { kind: "invalid-payload" };
	}
	return { kind: "accepted" };
}

export function validateDrainHostHello(value: unknown, expectedNonce: string): value is DrainHostHello {
	if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
	const candidate = value as Partial<DrainHostHello>;
	return (
		hasExactKeys(candidate, ["type", "version", "nonce"]) &&
		candidate.type === DRAIN_HOST_HELLO_TYPE &&
		candidate.version === DRAIN_HOST_PROTOCOL_VERSION &&
		candidate.nonce === expectedNonce
	);
}

/**
 * Child-side trust boundary for the initial parent hello. The child does not
 * know the nonce until it has received this message, so the helper validates
 * the wire shape and returns the nonce for subsequent status messages.
 */
export function validateDrainHostHelloEvent(
	event: Pick<MessageEvent, "origin" | "source" | "data">,
	expectation: Pick<DrainHostValidationExpectation, "origin" | "source">
): DrainHostHelloValidationResult {
	if (event.origin !== expectation.origin) return { kind: "origin-mismatch" };
	if (event.source !== expectation.source) return { kind: "source-mismatch" };
	if (typeof event.data !== "object" || event.data === null || Array.isArray(event.data)) return { kind: "invalid-payload" };
	const nonce = (event.data as Partial<DrainHostHello>).nonce;
	if (typeof nonce !== "string" || nonce.length === 0 || !validateDrainHostHello(event.data, nonce)) {
		return { kind: "invalid-payload" };
	}
	return { kind: "accepted", nonce };
}

export interface CreateDrainHostStatusInput {
	nonce: string;
	sequence: number;
	state: DrainHostState;
	pendingTotal: number;
	needsAttention: boolean;
	byKind: Readonly<Record<string, number>>;
}

export function createDrainHostStatus(input: CreateDrainHostStatusInput): DrainHostStatusMessage {
	if (!Number.isInteger(input.sequence) || input.sequence < 1) throw new Error("Drain-host sequence must be a positive integer");
	if (!Number.isInteger(input.pendingTotal) || input.pendingTotal < 0) throw new Error("Drain-host pendingTotal must be non-negative");
	if (typeof input.needsAttention !== "boolean") throw new Error("Drain-host needsAttention must be boolean");
	const byKind = validateByKind(input.byKind);
	return {
		type: DRAIN_HOST_STATUS_TYPE,
		version: DRAIN_HOST_PROTOCOL_VERSION,
		nonce: input.nonce,
		sequence: input.sequence,
		state: input.state,
		pendingTotal: input.pendingTotal,
		needsAttention: input.needsAttention,
		byKind
	};
}

function validateByKind(value: unknown): Readonly<Record<string, number>> {
	if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("Drain-host byKind must be an object");
	const result: Record<string, number> = {};
	for (const [kind, count] of Object.entries(value)) {
		if (!/^[a-z][a-z0-9_-]*$/.test(kind) || !Number.isInteger(count) || count < 0) {
			throw new Error("Drain-host byKind contains an invalid entry");
		}
		result[kind] = count;
	}
	return result;
}

function hasExactKeys(value: object, expectedKeys: readonly string[]): boolean {
	const keys = Object.keys(value).sort();
	return keys.length === expectedKeys.length && keys.every((key, index) => key === expectedKeys.slice().sort()[index]);
}

function isStatusMessage(value: unknown): value is DrainHostStatusMessage {
	if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
	const candidate = value as Partial<DrainHostStatusMessage>;
	if (
		!hasExactKeys(candidate, ["type", "version", "nonce", "sequence", "state", "pendingTotal", "needsAttention", "byKind"]) ||
		candidate.type !== DRAIN_HOST_STATUS_TYPE ||
		candidate.version !== DRAIN_HOST_PROTOCOL_VERSION ||
		typeof candidate.nonce !== "string" ||
		!Number.isInteger(candidate.sequence) ||
		(candidate.sequence as number) < 1 ||
		!(["idle", "draining", "yielded", "unavailable"] as const).includes(candidate.state as DrainHostState) ||
		!Number.isInteger(candidate.pendingTotal) ||
		(candidate.pendingTotal as number) < 0 ||
		typeof candidate.needsAttention !== "boolean"
	) {
		return false;
	}
	try {
		validateByKind(candidate.byKind);
		return true;
	} catch {
		return false;
	}
}

/**
 * Parent-side trust boundary. Origin and source are both identity checks; the
 * nonce binds the child to this embedding, while sequence rejects delayed
 * duplicate/replayed status messages. Only status-shaped data crosses it.
 */
export function validateDrainHostEvent(
	event: Pick<MessageEvent, "origin" | "source" | "data">,
	expectation: DrainHostValidationExpectation,
	state: DrainHostValidationState
): DrainHostValidationResult {
	if (event.origin !== expectation.origin) return { kind: "origin-mismatch" };
	if (event.source !== expectation.source) return { kind: "source-mismatch" };
	if (!isStatusMessage(event.data) || event.data.nonce !== expectation.nonce) return { kind: "invalid-payload" };
	if (event.data.sequence <= state.lastSequence) return { kind: "replay" };
	state.lastSequence = event.data.sequence;
	return { kind: "accepted", status: event.data };
}
